import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { CONSTANTS, FEATURE_FLAGS } from '../../../../constants/constants';
import React, { useContext, useEffect } from 'react';
import { VulnDrawerTabsContext } from '../../../../components/NRVulnerabilitiesTable/NRVulnerabilityDetails/NRVulnerabilityDetails';
import NRHCPVerification from '../../../../components/NRHCPVerification/NRHCPVerification';
import NREllipsisTooltip from '../../../../components/NREllipsisTooltip/NREllipsisTooltip';
import { useTheme } from '@mui/material/styles';
import NRMetricCard from '../../../../components/NRMetricCard/NRMetricCard';
import NRSBOMIcon from '../../../../NRIcons/NRSBOMIcon';
import { PACKAGE_MANAGER_ICONS } from '../../../../components/NRReactGridCustomComponents/NRCustomProviders/NRSBOMTypeProvider';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import OnDiskFalseIcon from '../../../../NRIcons/OnDiskFalseIcon';
import OnDiskTrueIcon from '../../../../NRIcons/OnDiskTrueIcon';
import { NRConfidenceFormatter } from '../../../../components/NRReactGridCustomComponents/NRCustomProviders/NRConfidenceProvider';
import { NRIdentificationFormatter } from '../../../../components/NRReactGridCustomComponents/NRCustomProviders/NRIdentificationProvider/NRIdentificationProvider';
import { useFlag } from '@openfeature/react-sdk';
import NRTagList from '../../../../components/NRTagList/NRTagList';
import { SBOM_CONSTANTS } from '../../../../components/SBOM/SBOM.constants';
import NRAiModelCategorySeverity from './NRAiModelCategorySeverity';
const {
  DESCRIPTION,
  IDENTIFIERS,
  VERIFIED_VIA,
  CONFIDENCE,
  ON_DISK,
  IS_PREDICTED,
  IDENTIFIED_VIA,
  SCOPE,
  AI_MODEL_DETAILS,
  RISK_CATEGORY,
  INFERENCE_CONFIDENCE,
  FORMAT,
  FRAMEWORK,
  DOMAIN,
  TASK,
  ARCHITECTURE,
  PARAMETERS
} = CONSTANTS.TABLE.SBOM_TABLE.SBOM_DETAIL_DRAWER;
const { aiModel } = CONSTANTS.TABLE.SBOM_TABLE.FILTER_MAP;

function NRSBOMDescription({ dependency, aiModelData, threshold, scrollTarget, index, scrollableId }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { setScrollSpyTabsValue } = useContext(VulnDrawerTabsContext);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold,
    target: !!scrollTarget ? scrollTarget : undefined
  });

  useEffect(() => {
    setScrollSpyTabsValue(index, trigger);
  }, [trigger]);

  const { name, vendor, version, type, package: dependencyPackage, license } = dependency?.dependency || {};
  const isAiModel = type === aiModel;
  const packageType = dependencyPackage?.type;
  const packageManagerKey = isAiModel ? type : packageType?.toUpperCase();
  const iconDefinition = (!!packageManagerKey && PACKAGE_MANAGER_ICONS[packageManagerKey]) || PACKAGE_MANAGER_ICONS.DEFAULT;
  const packageIcon = isDarkMode ? iconDefinition?.ICON_DARK || iconDefinition?.ICON : iconDefinition?.ICON;

  const { value: isComponentIdentifiedViaEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_IDENTIFIED_VIA, false);
  const { value: isComponentConfidenceEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_CONFIDENCE, false);
  const { value: isAiRelatedDependenciesEnabled } = useFlag(FEATURE_FLAGS.AI_RELATED_DEPENDENCIES, false);
  const aiModelAttributes = dependency?.aiModelAttributes || {};
  const aiModelDetails = aiModelAttributes?.modelAttributes || {};
  const modelCategory = aiModelAttributes?.modelCategory;
  const severity = aiModelAttributes?.severity ?? aiModelDetails?.severity ?? aiModelDetails?.riskScore;
  const {
    version: aiModelVersion,
    name: aiModelName,
    author: aiModelAuthor,
    organization: aiModelOrganization,
    description: aiModelDescription,
    license: aiModelLicense,
    aiModelArchitectureType
  } = aiModelData || {};
  const showAiModelData = isAiModel && !!aiModelData;
  const componentCaption = `${version?.id}${!!vendor ? ' - ' + vendor : ''}`;
  const aiComponentCaption = `${aiModelVersion}${!!aiModelAuthor ? ' - ' + aiModelAuthor : ''}`;

  return (
    <Box id={scrollableId}>
      <Box display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <NRMetricCard
            maxWidth={!!license.length ? '54%' : '70%'}
            metric={'Component'}
            result={showAiModelData ? aiModelName : name}
            icon={<NRSBOMIcon height={theme.spacing(4)} width={theme.spacing(4)} />}
            caption={showAiModelData ? aiComponentCaption : componentCaption}
          />
          <NRMetricCard
            minWidth={!!license.length ? '20%' : '30%'}
            metric={'Type'}
            result={SBOM_CONSTANTS.TYPE_COLUMN_MAP[type]}
            icon={React.cloneElement(packageIcon, {
              width: 32,
              height: 32
            })}
            caption={showAiModelData ? aiModelArchitectureType : !!iconDefinition.NAME ? iconDefinition.NAME : ''}
          />
          {!!license.length && (
            <NRMetricCard
              maxWidth={'54%'}
              minWidth="20%"
              metric={'License'}
              result={license.join(', ')}
              icon={<VerifiedOutlinedIcon sx={{ height: theme.spacing(4), width: theme.spacing(4) }} color={theme.palette.text.iconInfo} />}
              caption={showAiModelData ? aiModelLicense : ''}
            />
          )}
        </Stack>
      </Box>
      <Box mt={theme.spacing(3)}>
        <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
          {DESCRIPTION}
        </Typography>
        <Typography tabIndex={0} variant="body2" color="text.secondary" mb={theme.spacing(3)}>
          {showAiModelData ? aiModelDescription : dependency?.dependency?.description || 'No description yet.'}
        </Typography>
      </Box>
      {dependency?.dependency?.identifiers && !!dependency?.dependency?.identifiers.length && (
        <Box mt={theme.spacing(3)}>
          <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
            {IDENTIFIERS}
          </Typography>
          {dependency?.dependency?.identifiers.map((each, i) => (
            <Box key={`${i}-${each.type}-${each.uri}`} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography tabIndex={0} component="span" variant="body2" color="text.secondary" mr={theme.spacing(0.5)}>
                {each.type}:
              </Typography>
              <NREllipsisTooltip
                text={each.uri}
                content={
                  <Typography tabIndex={0} component={'span'} variant="body2" color="text.secondary" mb={theme.spacing(1)}>
                    {each.uri}
                  </Typography>
                }
                maxWidth={550}
              />
            </Box>
          ))}
        </Box>
      )}
      <Box display={'flex'} flexDirection={'row'} gap={theme.spacing(3)} flexWrap="wrap">
        {!isComponentIdentifiedViaEnabled && (
          <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={130}>
            <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
              {VERIFIED_VIA}
            </Typography>
            <NRHCPVerification verification={dependency.verification} justifyContent={'left'} />
          </Box>
        )}
        {isComponentIdentifiedViaEnabled && (
          <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={130}>
            <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
              {IDENTIFIED_VIA}
            </Typography>
            <NRIdentificationFormatter value={dependency.identifiedVia} showAll={true} />
          </Box>
        )}
        {isComponentConfidenceEnabled && (
          <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={130}>
            <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
              {CONFIDENCE}
            </Typography>
            <NRConfidenceFormatter value={dependency?.dependency?.confidence} />
          </Box>
        )}
        <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={130}>
          <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
            {ON_DISK}
          </Typography>
          <Box display="flex" alignItems="center" gap={theme.spacing(1)} height={24}>
            {dependency?.onDisk ? (
              <>
                <OnDiskTrueIcon color={theme.palette.info.main} width={20} height={20} />
                <Typography tabIndex={0} variant="body2" color="text.secondary" sx={{ lineHeight: theme.spacing(3) }}>
                  True
                </Typography>
              </>
            ) : (
              <>
                <OnDiskFalseIcon color={theme.palette.text.disabled} width={20} height={20} />
                <Typography tabIndex={0} variant="body2" color="text.secondary" sx={{ lineHeight: theme.spacing(3) }}>
                  False
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={130}>
          <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
            {IS_PREDICTED}
          </Typography>
          <Box display="flex" alignItems="center" gap={theme.spacing(1)} height={24}>
            {dependency?.isPredicted ? (
              <>
                <OnDiskTrueIcon color={theme.palette.info.main} width={20} height={20} />
                <Typography tabIndex={0} variant="body2" color="text.secondary" sx={{ lineHeight: theme.spacing(3) }}>
                  True
                </Typography>
              </>
            ) : (
              <>
                <OnDiskFalseIcon color={theme.palette.text.disabled} width={20} height={20} />
                <Typography tabIndex={0} variant="body2" color="text.secondary" sx={{ lineHeight: theme.spacing(3) }}>
                  False
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Box mt={theme.spacing(3)} minWidth={0}>
        <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
          {SCOPE}
        </Typography>
        <Box display="flex" height={24} alignItems="center">
          <NRTagList tags={dependency?.scope} isScope={true} showAll={true} />
        </Box>
      </Box>
      <Divider sx={{ mt: theme.spacing(3) }} />
      {isAiRelatedDependenciesEnabled && (
        <Box mt={theme.spacing(3)} minWidth={0}>
          <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
            {AI_MODEL_DETAILS}
          </Typography>
          <Box display={'flex'} flexDirection={'row'} gap={theme.spacing(3)} flexWrap="wrap">
            {!!modelCategory && (
              <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={150}>
                <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
                  {RISK_CATEGORY}
                </Typography>
                <NRAiModelCategorySeverity severity={severity} modelCategory={modelCategory} />
              </Box>
            )}
            {!!aiModelDetails?.inferenceConfidence && (
              <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={150}>
                <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
                  {INFERENCE_CONFIDENCE}
                </Typography>
                <NRConfidenceFormatter value={aiModelDetails.inferenceConfidence} />
              </Box>
            )}
          </Box>
          <Box display={'flex'} flexDirection={'row'} gap={theme.spacing(3)} flexWrap="wrap">
            {!!aiModelDetails?.format && (
              <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={150}>
                <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
                  {FORMAT}
                </Typography>
                <Typography tabIndex={0} variant="body2" color="text.secondary">
                  {aiModelDetails.format}
                </Typography>
              </Box>
            )}
            {!!aiModelDetails?.framework && (
              <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={150}>
                <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
                  {FRAMEWORK}
                </Typography>
                <Typography tabIndex={0} variant="body2" color="text.secondary">
                  {aiModelDetails.framework}
                </Typography>
              </Box>
            )}
            {!!aiModelDetails?.domain && (
              <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={150}>
                <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
                  {DOMAIN}
                </Typography>
                <Typography tabIndex={0} variant="body2" color="text.secondary">
                  {aiModelDetails.domain}
                </Typography>
              </Box>
            )}
          </Box>
          <Box display={'flex'} flexDirection={'row'} gap={theme.spacing(3)} flexWrap="wrap">
            {!!aiModelDetails?.task && (
              <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={150}>
                <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
                  {TASK}
                </Typography>
                <Typography tabIndex={0} variant="body2" color="text.secondary">
                  {aiModelDetails.task}
                </Typography>
              </Box>
            )}
            {!!aiModelDetails?.architecture && (
              <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={150}>
                <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
                  {ARCHITECTURE}
                </Typography>
                <Typography tabIndex={0} variant="body2" color="text.secondary">
                  {aiModelDetails.architecture}
                </Typography>
              </Box>
            )}
            {!!aiModelDetails?.parameters && (
              <Box mt={theme.spacing(3)} flex="1 1 0" minWidth={150}>
                <Typography tabIndex={0} variant="subtitle1" mb={theme.spacing(1)}>
                  {PARAMETERS}
                </Typography>
                <Typography tabIndex={0} variant="body2" color="text.secondary">
                  {aiModelDetails.parameters}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {isAiRelatedDependenciesEnabled && <Divider sx={{ mt: theme.spacing(3) }} />}
    </Box>
  );
}
export default NRSBOMDescription;
